import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { catchError, filter, finalize, map, of, switchMap, take, tap } from 'rxjs';
import { AddressService } from 'src/app/core/services/address.service';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { Address } from 'src/app/shared/models/address';
import { SnackbarService } from 'ok-angular-shared/components';
import { whitespace } from 'src/app/shared/validators/whitespace.validator';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { CreateDepartmentRequest, UpdateDepartmentRequest } from 'src/app/features/department/department.model';
import { Organization } from '../../../organization-models';
import { MatDialog } from '@angular/material/dialog';
import { DepartmentGroupAddOrEditComponent } from 'src/app/features/department/components/department-group-add-or-edit/department-group-add-or-edit.component';
import DepartmentGroupService from 'src/app/shared/services/department-group.service';
import { DepartmentGroup } from 'src/app/features/department/departmentGroup.model';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import { AdmG4BusinessAccountNumberComponent } from 'src/app/shared/components/adm-g4-business-account-number/adm-g4-business-account-number.component';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
  InitialAddress,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { EditDepartmentState } from '../create-organization-wizard.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatError, MatFormField, MatHint, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { Guid } from 'guid-typescript';

interface DepartmentsForm {
  departmentId: FormControl<number | null>;
  departmentName: FormControl<string>;
  invoicingAccountNumber: FormControl<number | null>;
  darId: FormControl<Guid | null>;
  departmentAddress: FormControl<string | null>;
  isHeadquarter: FormControl<boolean>;
  agreementNumber: FormControl<number | null>;
  departmentGroupId: FormControl<number | null>;
  addressWithoutDARValidation: FormControl<boolean>;
}

@Component({
  selector: 'adm-step2-department-form',
  templateUrl: './step2-department-form.component.html',
  styleUrls: ['./step2-department-form.component.scss'],
  imports: [
    DepAddressTypeaheadComponent,
    MatSlideToggle,
    MatHint,
    MatError,
    MatOption,
    MatSelect,
    MatLabel,
    MatFormField,
    MatTooltip,
    ReactiveFormsModule,
    MatIcon,
    MatButton,
    MatInput,
    AdmG4AgreementNumberComponent,
    AdmG4BusinessAccountNumberComponent,
  ],
})
export class Step2DepartmentFormComponent {
  private loadingService = inject(TopLoaderService);
  private snackbarService = inject(SnackbarService);
  private departmentService = inject(DepartmentService);
  private addressService = inject(AddressService);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);

  private departmentGroupService = inject(DepartmentGroupService);

  organization = input.required<Organization>();

  refreshStep2Event = output<{ departmentId: number; organizationId: number }>();

  updateDepartments = output<void>();

  nextStep = output<void>();

  editDepartmentId = input.required<EditDepartmentState>();

  editDepartmentId$ = toObservable(this.editDepartmentId)
    .pipe(
      filter(dep => dep.id != null && dep.timestamp > 0),
      switchMap(dep =>
        this.departmentGroupService
          .getDepartmentGroups(this.organization().id)
          .pipe(map(departmentGroups => ({ dep, departmentGroups: departmentGroups.items })))
      ),
      switchMap(({ dep, departmentGroups }) => {
        this.departmentGroups.set(departmentGroups);
        const departmentId = dep.id ?? 0;
        if (departmentId <= 0) {
          this.isInEditMode.set(false);
          return of(null);
        }

        return this.departmentService.getDepartment(departmentId).pipe(
          tap(department => {
            if (department) {
              this.departmentsForm = this.initForm();
              this.isInEditMode.set(true);
              this.departmentsForm.patchValue({
                departmentId: department.id,
                departmentName: department.name,
                invoicingAccountNumber: department.invoicingAccountNumber ?? null,
                agreementNumber: department.agreementNumber ?? null,
                isHeadquarter: department.isHeadquarter,
                departmentGroupId: department.departmentGroupId ?? null,
                addressWithoutDARValidation: !department.darId,
              });

              this.initialAddress.set({
                darId: department.darId,
                fullAddress: department.address,
              });

              this.departmentsForm.controls.departmentAddress.setValue(department.address);
              this.departmentsForm.controls.darId.setValue(department.darId);
            }
          })
        );
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  initForm = () => {
    return this.fb.group<DepartmentsForm>({
      departmentId: new FormControl(null),
      departmentName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, whitespace(), Validators.minLength(2), Validators.maxLength(30)],
      }),
      departmentAddress: new FormControl<string | null>(null),
      invoicingAccountNumber: new FormControl(null),
      darId: new FormControl(null),
      isHeadquarter: new FormControl<boolean>(false, { nonNullable: true }),
      agreementNumber: new FormControl(null),
      departmentGroupId: new FormControl(null),
      addressWithoutDARValidation: new FormControl<boolean>(false, { nonNullable: true }),
    });
  };

  isInEditMode = signal<boolean>(false);
  public departmentsForm: FormGroup<DepartmentsForm> = this.initForm();
  public showForm = false;
  departmentGroups = signal<DepartmentGroup[]>([]);

  toggleForm() {
    this.showForm = !this.showForm;
  }

  public g4Info = 'G4 Kontonr til for afdelingen.';
  public okAftaleNr =
    'Det aftalenr., der er oprettet til lokationen i G4 og som også bruges i projekt id. Aftalenummeret anvendes til evt. forbrugsopkrævning, strømgodtgørelse og evt. elafgiftrefusion.';

  public get isLoading(): boolean {
    return this.loadingService.loading;
  }

  public formatAddressToString(address: Address): string {
    return this.addressService.formatAddressToString(address);
  }

  public selectAddress(address: AddressChanged | null) {
    if (address !== null) {
      this.departmentsForm.controls.departmentAddress.setValue(address.formatAddressToString ?? null);
      this.departmentsForm.controls.darId.setValue(address.darId ?? null);
    }
  }

  public cancel() {
    this.departmentsForm = this.initForm();
    this.refreshStep2Event.emit({ departmentId: 0, organizationId: this.organization().id });
    this.isInEditMode.set(false);
    this.showForm = false;
  }
  public save() {
    this.loadingService.setLoading(true);

    if (this.departmentsForm.valid) {
      if (this.isInEditMode()) {
        this.updateDepartment();
      } else {
        this.createDepartment();
      }
    }
  }
  initialAddress = signal<InitialAddress | null>(null);

  private updateDepartment() {
    let departmentId: number | null = null;
    if (this.departmentsForm.controls.departmentId.value) {
      departmentId = this.departmentsForm.controls.departmentId.value;
    }
    let agreementNumber: number | null = null;
    let invoicingAccountNumber: number | null = null;
    if (this.departmentsForm.controls.agreementNumber.value) {
      agreementNumber = this.departmentsForm.controls.agreementNumber.value;
    }
    if (this.departmentsForm.controls.invoicingAccountNumber.value) {
      invoicingAccountNumber = this.departmentsForm.controls.invoicingAccountNumber.value;
    }
    const darValidationDisabled = this.departmentsForm.controls.addressWithoutDARValidation.value;

    const departmentObject: UpdateDepartmentRequest = {
      name: this.departmentsForm.controls.departmentName.value,
      invoicingAccount: invoicingAccountNumber,
      agreementNumber: agreementNumber,
      address: this.departmentsForm.controls.departmentAddress.value,
      darId: darValidationDisabled
        ? null
        : this.departmentsForm.controls.darId.value == null
          ? null
          : this.departmentsForm.controls.darId.value.toString(),
      isHeadquarter: this.departmentsForm.controls.isHeadquarter.value,
      departmentGroupId: this.departmentsForm.controls.departmentGroupId.value,
    };

    if (departmentId == null) {
      this.loadingService.setLoading(false);
      return;
    }

    this.departmentService
      .updateDepartment(departmentId, departmentObject)
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 100170) {
              this.snackbarService.warning(`Der findes allerede en afdeling med samme navn.`);
            }
          } else {
            // TODO: log error when able to
            this.snackbarService.error('Der opstod en ukendt fejl ved opdatering af afdeling.');
          }
          return of();
        }),
        finalize(() => this.loadingService.setLoading(false))
      )
      .subscribe(() => {
        this.snackbarService.success(`Ændringer til afdelingen blev gemt.`);
        this.departmentsForm = this.initForm();
        this.isInEditMode.set(false);
        this.refreshStep2Event.emit({ departmentId, organizationId: this.organization().id });

        this.updateDepartments.emit();
        this.showForm = false;
      });
  }

  private createDepartment() {
    const darValidationDisabled = this.departmentsForm.controls.addressWithoutDARValidation.value;

    const departmentObject: CreateDepartmentRequest = {
      name: this.departmentsForm.controls.departmentName.value,
      invoicingAccount: this.departmentsForm.controls.invoicingAccountNumber.value,
      agreementNumber: this.departmentsForm.controls.agreementNumber.value,
      address: this.departmentsForm.controls.departmentAddress.value,
      darId: darValidationDisabled
        ? null
        : this.departmentsForm.controls.darId.value == null
          ? null
          : this.departmentsForm.controls.darId.value.toString(),
      isHeadquarter: this.departmentsForm.controls.isHeadquarter.value,
      departmentGroupId: this.departmentsForm.controls.departmentGroupId.value,
    };
    this.departmentService
      .createDepartment(this.organization().id, departmentObject)
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 100170) {
              this.snackbarService.warning(`Der findes allerede en afdeling med samme navn.`);
            }
          } else {
            // TODO: log error when able to
            this.snackbarService.error('Der opstod en ukendt fejl ved oprettelse af afdeling.');
          }
          return of();
        }),
        finalize(() => this.loadingService.setLoading(false))
      )
      .subscribe(departmentId => {
        if (departmentId) {
          this.snackbarService.success(`Afdelingen blev oprettet.`);

          this.departmentsForm = this.initForm();
          this.refreshStep2Event.emit({ departmentId: departmentId, organizationId: this.organization().id });
        }
        this.updateDepartments.emit();
        this.showForm = false;
      });
  }

  public getControlErrorMessage(fieldIdentifier: string, error: string) {
    let fieldName = 'Navn';
    switch (fieldIdentifier) {
      case 'departmentName':
        fieldName = `Afdelingsnavn`;
        break;
      case 'addressLookup':
        fieldName = `Adresse`;
        break;
    }

    switch (error) {
      case 'number':
        return `${fieldName} skal være et heltal.`;
      case 'minLength':
        return `${fieldName} skal være på mindst 2 karakterer.`;
      case 'maxLength':
        return `${fieldName} må maks være 30 karakterer.`;
      case 'required':
        return `${fieldName} er påkrævet.`;
      case 'empty':
        return `${fieldName} må ikke være tom.`;
    }
  }

  handleNextStep() {
    this.nextStep.emit();
  }

  openDepartmentGroupDialog() {
    const dialog = this.dialog.open(DepartmentGroupAddOrEditComponent, {
      width: '600px',
      data: { organizationId: this.organization().id },
    });

    dialog
      .afterClosed()
      .pipe(take(1))
      .pipe(
        switchMap(() => this.departmentGroupService.getDepartmentGroups(this.organization().id)),
        map(departmentGroups => departmentGroups.items)
      )
      .subscribe(departmentGroups => {
        this.departmentGroups.set(departmentGroups);
      });
  }
}
